import { Environment, Platform } from '@foundation-player/runtime';
import { ClientAndScope } from '@foundation-player/types';
import { BuilderConfig } from '../../builder-config';
import { PluginConfigs } from '../../plugin-configs';
import { addAdvertisingPlugin } from './add-advertising-plugin';
import { addBitmovinAnalyticsPlugin } from './add-bitmovin-analytics-plugin';
import { addConcurrentStreamCheckPlugin } from './add-concurrent-stream-check-plugin';
import { addDeviceHeaderPlugin } from './add-device-header-plugin';
import { addEntitlementPlugin } from './add-entitlement-plugin';
import { addHeartbeatPlugin } from './add-heartbeat-plugin';
import { addNielsenPlugin } from './add-nielsen-plugin';
import { addNPAWAnalyticsPlugin } from './add-npaw-analytics-plugin';
import { addOnScreenLogPlugin } from './add-on-screen-log-plugin';
import { addSsaiPlugin } from './add-ssai-plugin';
import { addStreamFallbackPlugin } from './add-stream-fallback-plugin';
import { addVisibilityPlugin } from './add-visibility-plugin';

export type TransformPluginConfigs = (
  partialBuilderConfig: Omit<BuilderConfig, 'pluginConfigs'>,
  pluginConfigs: PluginConfigs,
  clientAndScope?: ClientAndScope,
  environment?: Environment,
  platform?: Platform,
) => PluginConfigs;

/**
 * This function is used to transform the plugin configs before it is passed to the builder.
 *
 * @remarks
 * The purpose is to define all transformers here and call them in order.
 */
export const transformPluginConfigs: TransformPluginConfigs = (
  partialBuilderConfig,
  pluginConfigs,
  clientAndScope,
  environment,
  platform,
) => {
  let transformedPluginConfigs = pluginConfigs;

  const transformers: TransformPluginConfigs[] = [
    addDeviceHeaderPlugin,
    addNielsenPlugin,
    addHeartbeatPlugin,
    addStreamFallbackPlugin,
    addBitmovinAnalyticsPlugin,
    addNPAWAnalyticsPlugin,
    addSsaiPlugin,
    addAdvertisingPlugin,
    addVisibilityPlugin, // IMPORTANT!: This transformer must be placed after the advertising plugin transformer
    addEntitlementPlugin,
    addConcurrentStreamCheckPlugin,
    addOnScreenLogPlugin,
  ];

  for (const transformer of transformers) {
    transformedPluginConfigs = transformer(
      partialBuilderConfig,
      transformedPluginConfigs,
      clientAndScope,
      environment,
      platform,
    );
  }

  return transformedPluginConfigs;
};
